import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { api, changeSession, onSessionChange, refreshSession, setAccessToken } from '../api/client.js';
import { homeMemory, homeSessionKey } from '../utils/home-memory.js';

export const useAuthStore = defineStore('auth', () => {
  const user = ref(null);
  const permissions = ref([]);
  const initialized = ref(false);
  const loading = ref(false);

  const authenticated = computed(() => Boolean(user.value));
  const isAdmin = computed(() => Boolean(user.value?.isPlatformAdmin));
  const canInvite = computed(() => isAdmin.value || permissions.value.includes('employees.invite'));
  const canManageEmployees = computed(() => isAdmin.value || permissions.value.includes('employees.manage'));
  const canViewEmployeeSensitive = computed(() => isAdmin.value || permissions.value.includes('employees.sensitive'));
  const canCreateEmployees = computed(() => hasPermission('employees.create'));
  const canOnboardEmployees = computed(() => hasPermission('employees.onboard'));
  const canViewEmployeeDirectory = computed(() => ['employees.manage', 'employees.sensitive', 'employees.create', 'employees.onboard', 'employees.delete', 'employees.invite'].some(hasPermission));

  function acceptSession(session) {
    const nextPermissions = session?.effectivePermissions || session?.user?.effectivePermissions || [];
    if (homeSessionKey(user.value, permissions.value) !== homeSessionKey(session?.user, nextPermissions)) homeMemory.clear();
    user.value = session?.user || null;
    permissions.value = nextPermissions;
    setAccessToken(session?.accessToken || null);
  }

  onSessionChange(acceptSession);

  async function bootstrap() {
    if (initialized.value) return;
    loading.value = true;
    try {
      await refreshSession();
    } catch {
      if (!user.value) acceptSession(null);
    } finally {
      initialized.value = true;
      loading.value = false;
    }
  }

  async function login(credentials) {
    loading.value = true;
    try {
      const data = await changeSession(async () => (await api.post('/auth/login', credentials, { skipAuthRefresh: true })).data);
      initialized.value = true;
      return data;
    } finally {
      loading.value = false;
    }
  }

  async function completeInvitation(token, form) {
    const data = await changeSession(async () => (await api.post(`/invitations/${encodeURIComponent(token)}/register`, form, {
      skipAuthRefresh: true
    })).data);
    initialized.value = true;
    return data;
  }

  async function logout() {
    try {
      await changeSession(() => api.post('/auth/logout', null, { skipAuthRefresh: true }), { logout: true });
    } finally {
      initialized.value = true;
    }
  }

  function hasPermission(permission) {
    return isAdmin.value || permissions.value.includes('*') || permissions.value.includes(permission);
  }

  return {
    user,
    permissions,
    initialized,
    loading,
    authenticated,
    isAdmin,
    canInvite,
    canManageEmployees,
    canCreateEmployees,
    canOnboardEmployees,
    canViewEmployeeDirectory,
    canViewEmployeeSensitive,
    bootstrap,
    login,
    logout,
    completeInvitation,
    hasPermission,
    acceptSession
  };
});
